import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getStoredConsent, setStoredConsent, applyConsent, type ConsentChoice } from '@/lib/tracking';

// A plain first-party banner, not a third-party CMP (Termly/OneTrust/etc.) —
// rxk has no EU audience or legal requirement for one, this exists purely so
// Google's Consent Mode setup wizard has a real banner to point at. Shown
// once per browser until a choice is made; TrackingScripts.tsx reads the
// same stored choice to set GA4/Meta's default consent state on load.
export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  // Staff console, not the storefront — no visitor tracking to consent to.
  const isAdmin = useLocation().pathname.startsWith('/admin');

  useEffect(() => {
    setVisible(getStoredConsent() === null);
  }, []);

  const choose = (value: ConsentChoice) => {
    setStoredConsent(value);
    applyConsent(value);
    setVisible(false);
  };

  if (!visible || isAdmin) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-[100] bg-[#2A2A2A] text-white px-3 py-2 sm:px-4 sm:py-3">
      {/* One row at every width — stacking text over buttons on mobile
          made the banner cover a big chunk of the screen. */}
      <div className="max-w-5xl mx-auto flex flex-row items-center gap-2.5 sm:gap-4">
        <p className="text-[11px] sm:text-[12.5px] leading-snug sm:leading-relaxed flex-1 min-w-0 text-left">
          <span className="sm:hidden">We use cookies.</span>
          <span className="hidden sm:inline">We use cookies to improve your experience and measure how our site is used.</span> See our{' '}
          <Link to="/privacy-policy" className="underline hover:text-white/80">Privacy Policy</Link>.
        </p>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => choose('denied')}
            className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-[11px] sm:text-[12.5px] font-semibold border border-white/30 hover:bg-white/10 cursor-pointer whitespace-nowrap"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => choose('granted')}
            className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-[11px] sm:text-[12.5px] font-semibold bg-[#7B1113] hover:bg-[#5C0D0F] cursor-pointer whitespace-nowrap"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
